import React, { useEffect } from 'react'
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  LinearProgress
} from '@mui/material'
import { StepHeader } from '../../components'
import CheckIcon from '@mui/icons-material/Check'
import { stepperStyles } from '../styles'
import {
  getAvailablePositions,
  getTakenPositions,
  getFinishedPositions,
  getInventoryCheckUserDetail
} from '../../api'
import { useApi } from '../../../../hooks/useApi'
import { Status } from '../../../../hooks/useFetchState'
import { ApiResponse } from '../../../../typings/ApiResponse'
import { StepProps } from '../../../../components/_WMS/Stepper'
import { InventoryCheckPosition, SharedStateType } from '../../types'

interface AuditDetail {
  inventoryCheckDetailId: number
  positionName: string
  startDate: string | null
  endDate: string | null
  cycle: number
  userAlias: string
}

interface AuditSummary {
  warehouseName?: string
  zoneName?: string
  totalPosition?: number
  pendingPositions?: number
  auditedPositions?: number
  progress?: number
  details?: AuditDetail[]
}

interface TakenPosition extends InventoryCheckPosition {
  startDate?: string
  endDate?: string
  progress?: number
  userAlias?: string
}

const formatTime = (dateStr: string): string => {
  try {
    const d = new Date(dateStr)
    return d.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })
  } catch {
    return ''
  }
}

export const InventoryCheckDetailScreen: React.FC<
  StepProps<SharedStateType>
> = ({ sharedState: { workOrderId, user }, setSharedState }) => {
  const { state: availableState, callApi: callAvailableApi } =
    useApi<ApiResponse<InventoryCheckPosition>>()
  const { state: takenState, callApi: callTakenApi } =
    useApi<ApiResponse<TakenPosition>>()
  const { state: finishedState, callApi: callFinishedApi } =
    useApi<ApiResponse<TakenPosition>>()
  const { state: summaryState, callApi: callSummaryApi } =
    useApi<AuditSummary>()

  useEffect(() => {
    if (!workOrderId) return
    callAvailableApi(getAvailablePositions(workOrderId, user?.userId)).catch(
      () => undefined
    )
    callTakenApi(getTakenPositions(workOrderId, user?.userId)).catch(
      () => undefined
    )
    callFinishedApi(getFinishedPositions(workOrderId, user?.userId)).catch(
      () => undefined
    )
    callSummaryApi(
      getInventoryCheckUserDetail(workOrderId, user?.userId)
    ).catch(() => undefined)
  }, [])

  useEffect(() => {
    setSharedState((prev) => ({ ...prev, stepTitle: 'Detalle de auditoría' }))
    return () => {
      setSharedState((prev) => ({ ...prev, stepTitle: undefined }))
    }
  }, [])

  const summary = summaryState.data
  const finishedPositions = finishedState.data?.items ?? []
  const inProgressPositions = takenState.data?.items ?? []
  const availablePositions = availableState.data?.items ?? []

  const finishedIds = new Set(finishedPositions.map((p) => p.positionId))
  const inProgressIds = new Set(inProgressPositions.map((p) => p.positionId))

  const allPositions: Array<
    TakenPosition & { _state: 'auditada' | 'enProceso' | 'pendiente' }
  > = [
    ...finishedPositions.map((p) => ({ ...p, _state: 'auditada' as const })),
    ...inProgressPositions
      .filter((p) => !finishedIds.has(p.positionId))
      .map((p) => ({ ...p, _state: 'enProceso' as const })),
    ...availablePositions
      .filter(
        (p) =>
          !finishedIds.has(p.positionId) && !inProgressIds.has(p.positionId)
      )
      .map((p) => ({ ...p, _state: 'pendiente' as const }))
  ]

  const totalPosition = summary?.totalPosition ?? allPositions.length

  // Calcula auditadas contando solo posiciones donde TODOS los ciclos tienen endDate
  const auditadas = summary?.details
    ? (() => {
        const byPosition = new Map<string, typeof summary.details>()
        summary.details!.forEach((d) => {
          byPosition.set(d.positionName, [
            ...(byPosition.get(d.positionName) ?? []),
            d
          ])
        })
        let count = 0
        byPosition.forEach((cycles) => {
          if (cycles.every((c) => c.endDate !== null)) count++
        })
        return count
      })()
    : finishedPositions.length

  // Usa los datos del details para el porcentaje real, ignorando summary.progress que puede superar 100
  const progressPct =
    totalPosition > 0 ? Math.round((auditadas / totalPosition) * 100) : 0

  const isLoading =
    availableState.status === Status.Loading ||
    takenState.status === Status.Loading ||
    finishedState.status === Status.Loading

  const stateConfig = {
    auditada: {
      label: 'Auditada',
      chipBg: '#dcfce7',
      chipColor: '#16a34a',
      rowBg: '#f0fdf4',
      border: '#86efac',
      textColor: '#16a34a'
    },
    enProceso: {
      label: 'En proceso',
      chipBg: '#dbeafe',
      chipColor: '#1d4ed8',
      rowBg: '#eff6ff',
      border: '#93c5fd',
      textColor: '#1d4ed8'
    },
    pendiente: {
      label: 'Pendiente',
      chipBg: '#f0f0f0',
      chipColor: '#666',
      rowBg: '#f5f5f5',
      border: '#e5e7eb',
      textColor: '#666'
    }
  }

  return (
    <Box sx={stepperStyles.root}>
      <Box
        sx={stepperStyles.content}
        style={{
          justifyContent: 'flex-start',
          paddingTop: 24,
          overflowY: 'auto'
        }}
      >
        <Box style={{ width: '100%', padding: '0 16px' }}>
          {/* Cabecera */}
          <StepHeader
            loading={summaryState.status === Status.Loading}
            fields={[
              { label: 'Conteo', value: workOrderId ?? '—' },
              { label: 'Almacén', value: summary?.warehouseName },
              { label: 'Zona', value: summary?.zoneName }
            ]}
          />

          {/* Progreso — solo posiciones finalizadas */}
          <Box
            style={{
              background: '#fff',
              borderRadius: 12,
              padding: '12px 16px',
              boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
              marginBottom: 16
            }}
          >
            <Box
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 8
              }}
            >
              <Typography
                variant='caption'
                style={{ color: '#666', fontWeight: 600 }}
              >
                Ubicaciones auditadas
              </Typography>
              <Typography
                variant='caption'
                style={{ color: '#1d4ed8', fontWeight: 700 }}
              >
                {auditadas} / {totalPosition} • {progressPct}%
              </Typography>
            </Box>
            <LinearProgress
              variant='determinate'
              value={progressPct}
              style={{ borderRadius: 4, height: 6 }}
            />
          </Box>

          {/* Lista de posiciones */}
          <Typography
            variant='caption'
            style={{
              color: '#999',
              fontWeight: 700,
              letterSpacing: 1,
              textTransform: 'uppercase',
              display: 'block',
              marginBottom: 8
            }}
          >
            Ubicaciones
          </Typography>

          {isLoading && (
            <Box
              style={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: 16
              }}
            >
              <CircularProgress size={24} />
            </Box>
          )}

          {!isLoading &&
            allPositions.map((position) => {
              const cfg = stateConfig[position._state]
              const isAuditada = position._state === 'auditada'

              return (
                <Box
                  key={`${position.positionId}-${position._state}`}
                  style={{
                    background: cfg.rowBg,
                    border: `1px solid ${cfg.border}`,
                    borderRadius: 12,
                    marginBottom: 10,
                    padding: '12px 16px'
                  }}
                >
                  <Box
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: isAuditada ? 6 : 0
                    }}
                  >
                    <Typography
                      variant='body2'
                      style={{ fontWeight: 700, color: cfg.textColor }}
                    >
                      {position.positionName}
                    </Typography>
                    <Chip
                      icon={
                        isAuditada ? (
                          <CheckIcon
                            style={{ fontSize: 13, color: cfg.chipColor }}
                          />
                        ) : undefined
                      }
                      label={cfg.label}
                      size='small'
                      style={{
                        backgroundColor: cfg.chipBg,
                        color: cfg.chipColor,
                        fontWeight: 600,
                        height: 22
                      }}
                    />
                  </Box>

                  {isAuditada && (
                    <Box style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                      {position.endDate && (
                        <Typography variant='caption' style={{ color: '#666' }}>
                          Finalizada: {formatTime(position.endDate)}
                        </Typography>
                      )}
                      {position.progress !== undefined && (
                        <Typography
                          variant='caption'
                          style={{ color: '#16a34a', fontWeight: 700 }}
                        >
                          {position.progress}%
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              )
            })}
        </Box>
      </Box>
    </Box>
  )
}
